import { ConfigProvider, theme as antdTheme, ThemeConfig } from 'antd';
import clsx from 'clsx';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

import { iframeEmail } from '../../../atoms/iframeEmail';
import IFrameModal from '../../../components/IFrame/IFrameModal';
import PDFReaderIframe from '../../../components/IFrame/PDFReaderIframe';
import { pageTitle } from '../../../components/shared/basic/customHead';
import { ShouldRender } from '../../../components/shared/basic/ShouldRender';
import CustomLoader from '../../../components/shared/Loading';
import { checkAffiliateCode } from '../../../services/IFrame/apis';

const PDFReader: React.FC = () => {
  const [visible, setVisible] = useRecoilState(iframeEmail);
  const [loading, setLoading] = useState(true);
  const [checkCode, setCheckCode] = useState(false);
  const router = useRouter();
  const { code, theme } = router.query;

  const config: ThemeConfig = {
    token: {
      colorPrimary: '#FF5612',
      fontFamily: 'inherit',
    },
  };

  const onCancelCloseModal = () => {
    setVisible(false);
  };

  const handleCheckAffiliateCode = async (code: string) => {
    try {
      setLoading(true);
      const response = await checkAffiliateCode({ code });
      setCheckCode(response === 'Code Found');
    } catch (error) {
      console.error('API call error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.body.style.backgroundColor = !theme ? '#000000' : 'white';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, [theme]);

  useEffect(() => {
    if (code) {
      handleCheckAffiliateCode(code as string);
    }
  }, [code]);

  return (
    <>
      <Head>
        <title>{pageTitle('PDF Reader')}</title>
        <meta http-equiv='Permissions-Policy' content='fullscreen=()'></meta>
      </Head>
      <ConfigProvider
        theme={{
          ...config,
          algorithm: [antdTheme.compactAlgorithm],
        }}
      >
        <ShouldRender check={loading}>
          <CustomLoader loaderColor={!theme ? 'white' : 'black'} />
        </ShouldRender>
        <ShouldRender check={code && checkCode && !loading}>
          <div
            className={clsx(
              !theme ? 'bg-readerLightGrayBg' : '',
              'relative overflow-hidden ',
            )}
          >
            <span className='no-select'>
              <PDFReaderIframe isIFrame={true} theme={theme as string} />
            </span>
          </div>
        </ShouldRender>
        <ShouldRender check={(!code || !checkCode) && !loading}>
          <div
            className={clsx(
              !theme ? 'text-white' : 'text-gray-900',
              'relative overflow-hidden h-screen flex flex-col items-center justify-center',
            )}
          >
            <p className=' font-semibold text-primary text-3xl'>400</p>
            <h1 className='mt-3 text-3xl font-bold tracking-tight text-fadedWhite sm:text-4xl'>
              Something went wrong!
            </h1>
            <span className='text-lg mt-2'>
              {' '}
              Please verify your source link, as there seems to be a potential
              issue.
            </span>
          </div>
        </ShouldRender>

        <IFrameModal
          onCancel={onCancelCloseModal}
          visible={visible}
          isIFrame={true}
        />
      </ConfigProvider>
    </>
  );
};

export default PDFReader;
